import { EMPTY, Observable, of, timer } from "rxjs"
import { AnyAction } from "redux"
import { isActionOf } from "typesafe-actions"
import { catchError, filter, switchMap, takeUntil } from "rxjs/operators"
import { ActionsObservable, StateObservable } from "redux-observable"

// Models
import { EpicDependencies, RootState } from "../../../../models"

// Constants
import { ACTIVE, PING } from "../../constants"

// Actions
import * as a from "../actions"
import { fetchAvailableRecordsAsync, fetchInProgressRecordAsync, fetchStatsAsync } from "../../../doctor-stats/store/actions"
import { recordConflict } from "../../../records/store/actions"

/**
 * The server has taken the doctor's record away from them — another reviewer
 * picked it up, or an admin reassigned it.
 *
 * This used to be handled inside WSService by setting a plain module-level
 * `myInitObject.data = "true"`. Redux carries it now, which is what makes it a
 * one-shot: `recordConflict` is reset by every successful record fetch and by
 * `clearCurrentRecord` on unmount, so it can no longer sit latched while the
 * doctor is on the dashboard and then eject them from the NEXT record they open.
 */
const DOCTOR_UNASSIGNED = "doctorUnassignedWSEvent";


/**
 * @param action$
 * @param _
 * @param d
 */
export const openSingleRecordConnectionEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.openSingleRecordWSConnectionAsync.request)),
        switchMap(() => {
            d.wsService.open("handler");

            return new Observable(observer => {
                // <RecordPageContainer/> already watches `recordConflict` and
                // leaves the record when it rises — the same exit the HTTP-level
                // conflict takes, so there is one code path for "this record is
                // no longer yours" instead of two.
                d.wsService.register(() => {
                    observer.next(recordConflict());
                }, [DOCTOR_UNASSIGNED]);
                d.wsService.register(() => {
                    observer.next(a.openSingleRecordWSConnectionAsync.success());
                }, ["open"]);
            }) as Observable<AnyAction>
        }),
        catchError(err => {
            console.error(err);

            return EMPTY
        }),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const closeSingleRecordWSConnectionEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.closeSingleRecordWSConnection)),
        switchMap(({ payload: { code, reason } }) => {
            const socket = d.wsService.getSocket();

            if (socket !== null && socket.readyState === WebSocket.OPEN) {
                d.wsService.close(code, reason);
            }

            return EMPTY
        }),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const openAvailableRecordsConnectionEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.openAvailableRecordsWSConnectionAsync.request)),
        switchMap(() => {
            d.wsService.open("handler-list");

            return new Observable(observer => {
                d.wsService.register(() => {
                    observer.next(fetchInProgressRecordAsync.request());
                    observer.next(fetchAvailableRecordsAsync.request());
                }, ["updateDoctorListEvent"]);
                /*
                 * The dashboard's answer to being unassigned is to re-read the
                 * queue — the record in question is simply no longer the
                 * doctor's. It used to be `window.location.reload()`, fired from
                 * a component effect whose dependency was a mutable module
                 * property, so it went off on whatever render happened to come
                 * next and read as the page crashing.
                 */
                d.wsService.register(() => {
                    observer.next(fetchInProgressRecordAsync.request());
                    observer.next(fetchAvailableRecordsAsync.request());
                }, [DOCTOR_UNASSIGNED]);
                d.wsService.register(() => {
                    observer.next(a.openAvailableRecordsWSConnectionAsync.success());
                }, ["open"]);
            }) as Observable<AnyAction>
        }),
        catchError(err => {
            console.error(err);

            return EMPTY
        }),
    )
};

/**
 * @param action$
 * @param _
 * @param d
 */
export const closeAvailableRecordsWSConnectionEpic = (action$: ActionsObservable<AnyAction>, _: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.closeAvailableRecordsWSConnection)),
        switchMap(({ payload: { code, reason } }) => {
            const socket = d.wsService.getSocket();

            if (socket !== null && socket.readyState === WebSocket.OPEN) {
                d.wsService.close(code, reason);
            }

            return EMPTY
        }),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const runWSPingEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf([a.openSingleRecordWSConnectionAsync.success, a.openAvailableRecordsWSConnectionAsync.success])),
        switchMap(() => timer(0, 4000).pipe(
            takeUntil(action$.pipe(filter(isActionOf([a.closeSingleRecordWSConnection, a.closeAvailableRecordsWSConnection])))),
            switchMap(() => {
                const socket = d.wsService.getSocket();

                if (socket !== null && socket.readyState === WebSocket.OPEN) {
                    socket.send(PING);
                }

                return EMPTY
            }),
            catchError(err => of(a.pingError(err))),
        )),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const runListenerEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf([a.openSingleRecordWSConnectionAsync.success])),
        switchMap(() => timer(0, 1000).pipe(
            takeUntil(action$.pipe(filter(isActionOf([a.closeSingleRecordWSConnection])))),
            switchMap(() => {
                const socket = d.wsService.getSocket();

                if (socket !== null && socket.readyState === WebSocket.OPEN) {
                    d.wsService.addEventListeners();

                }

                return EMPTY
            }),
            catchError(err => of(a.pingError(err))),
        )),
    )
};


/**
 * @param action$
 * @param state$
 * @param d
 */
export const runWSCustomPingEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf([a.openSingleRecordWSConnectionAsync.success])),
        switchMap(() => timer(0, 60 * 1000).pipe(
            takeUntil(action$.pipe(filter(isActionOf([a.closeSingleRecordWSConnection])))),
            switchMap(() => {
                const socket = d.wsService.getSocket();

                if (socket !== null && socket.readyState === WebSocket.OPEN) {
                    socket.send(ACTIVE);
                }

                return EMPTY
            }),
            catchError(err => of(a.pingError(err))),
        )),
    )
};